import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Lock, Calendar, MapPin, Users, AlertCircle, ArrowLeft, CheckCircle } from "lucide-react";
import { API } from "../App";
import { Button } from "../components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentPage() {
  const { quoteId } = useParams();
  const [search] = useSearchParams();
  const cancelled = search.get("cancelled") === "1";
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/payments/quote/${quoteId}`)
      .then((res) => { if (alive) setQuote(res.data); })
      .catch((err) => { if (alive) setError(err.response?.data?.detail || "Booking not found"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [quoteId]);

  const startCheckout = async () => {
    setRedirecting(true);
    try {
      const res = await axios.post(`${API}/payments/checkout/session`, {
        quote_id: quoteId,
        origin_url: window.location.origin,
      });
      // Hard redirect to Stripe Checkout
      window.location.href = res.data.url;
    } catch (err) {
      setRedirecting(false);
      toast.error(err.response?.data?.detail || "Could not start payment");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center"><div className="spinner" /></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#0F0F0F] border border-red-500/20 rounded-sm p-8 text-center" data-testid="pay-error">
          <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h2 className="text-white text-lg font-semibold mb-2">We couldn't load this booking</h2>
          <p className="text-white/50 text-sm mb-4">{error}</p>
          <Link to="/" className="text-[#C5A059] hover:underline text-sm">Back to home</Link>
        </div>
      </div>
    );
  }

  const alreadyPaid = !!quote?.paid;
  const payable = ["awaiting_payment", "pending"].includes(quote?.status) && !alreadyPaid;

  return (
    <div className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/5 bg-[#0A0A0A]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold logo-text">TN</Link>
          <span className="text-xs text-white/50 uppercase tracking-wider flex items-center gap-1">
            <Lock className="w-3 h-3" /> Secure Stripe Payment
          </span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/" className="text-white/50 hover:text-white text-sm flex items-center gap-1 mb-8" data-testid="pay-back-home">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>

        <div className="bg-[#0F0F0F] border border-white/5 rounded-sm overflow-hidden" data-testid="payment-card">
          <div className="bg-gradient-to-br from-[#C5A059]/20 to-transparent p-6 border-b border-white/5">
            <p className="text-[#C5A059] text-xs uppercase tracking-widest mb-2">Booking {quote?.quote_id}</p>
            <h1 className="text-2xl sm:text-3xl text-white font-bold flex items-center flex-wrap gap-2">
              <MapPin className="w-5 h-5 text-[#C5A059]" />
              <span>{quote?.origin}</span>
              <span className="text-white/40">→</span>
              <span>{quote?.destination}</span>
            </h1>
            <p className="text-white/50 text-sm mt-2">Hi {quote?.user_name || "there"}, here are your booking details.</p>
          </div>

          <div className="p-6 grid grid-cols-2 gap-4 text-sm border-b border-white/5">
            <div>
              <p className="text-white/40 text-xs uppercase mb-1">Pickup</p>
              <p className="text-white flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[#C5A059]" /> {quote?.pickup_date} {quote?.pickup_time}</p>
            </div>
            <div>
              <p className="text-white/40 text-xs uppercase mb-1">Passengers</p>
              <p className="text-white flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[#C5A059]" /> {quote?.passengers ?? "-"}</p>
            </div>
            {quote?.is_round_trip && (
              <div className="col-span-2">
                <p className="text-white/40 text-xs uppercase mb-1">Return</p>
                <p className="text-white">{quote.return_date} {quote.return_time}</p>
              </div>
            )}
          </div>

          <div className="p-6 flex items-center justify-between border-b border-white/5">
            <span className="text-white/60 uppercase text-xs tracking-widest">Total to pay</span>
            <span className="text-3xl font-bold text-[#C5A059] font-mono" data-testid="pay-total-amount">€{Number(quote?.total_price || 0).toFixed(2)}</span>
          </div>

          <div className="p-6 space-y-4">
            {alreadyPaid && (
              <div className="flex items-center gap-2 text-emerald-400 text-sm bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3" data-testid="pay-already-paid">
                <CheckCircle className="w-4 h-4" /> This booking has already been paid. Thank you!
              </div>
            )}
            {!payable && !alreadyPaid && (
              <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/5 border border-amber-500/20 rounded-sm p-3" data-testid="pay-not-payable">
                <AlertCircle className="w-4 h-4" /> This booking is currently in &ldquo;{quote?.status}&rdquo; status and cannot be paid yet. Our team will email you when it&rsquo;s ready.
              </div>
            )}
            {cancelled && payable && (
              <div className="flex items-center gap-2 text-amber-400 text-sm bg-amber-500/5 border border-amber-500/20 rounded-sm p-3" data-testid="pay-cancelled-banner">
                <AlertCircle className="w-4 h-4" /> Payment was cancelled. You can try again below.
              </div>
            )}
            {payable && (
              <Button
                onClick={startCheckout}
                disabled={redirecting}
                className="btn-gold w-full text-base py-6"
                data-testid="pay-now-button"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                {redirecting ? "Redirecting to Stripe..." : `Pay €${Number(quote?.total_price || 0).toFixed(2)} with Stripe`}
              </Button>
            )}
            <p className="text-white/40 text-xs text-center mt-2 flex items-center justify-center gap-2">
              <Lock className="w-3 h-3" /> We accept cards, Apple Pay, Google Pay, SEPA & Klarna. Your payment never touches our servers.
            </p>
            {quote?.user_name && BACKEND_URL && (
              <p className="text-white/30 text-[10px] text-center">
                Powered by Stripe Checkout
              </p>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
